// @ts-nocheck
import { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';

import { createFormSchema, createInitialFormValues } from '../utils/formUtils';
import {
  HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES,
  validateDeploymentDate,
  validateDeploymentDateCommunication,
} from '../utils/deploymentDateUtils';
import { isValueEmpty } from '../utils/Utils';
import {
  CHECKLIST_TYPES,
  CHECKLIST_DATA_TYPE,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
} from '../const/CommonConst';
/**
 * Custom hook for managing single attribute edit form with Formik validation
 * @param {Object} params - Hook parameters
 * @param {Object} params.attribute - The attribute being edited
 * @param {any} params.value - Current value of the attribute
 * @param {Function} params.onSave - Callback function when form is saved
 * @param {Function} params.onCancel - Callback function when form is cancelled
 * @param {Object} params.formDetails - Template form details for validation
 * @param {Object} params.attributeValues - Current attribute values
 * @param {boolean} params.isHDFCUAMEnabled - Flag indicating if HDFCUAM is enabled
 * @param {Object} params.currentModelStatus - Current status of the model (for HDFCUAM validation)
 * @param {Object} params.entityData - Entity information
 * @returns {Object} - Hook return object with form state and handlers
 */
const useAttributeEditForm = ({
  attribute,
  value,
  onSave,
  onCancel,
  formDetails,
  attributeValues = EMPTY_OBJECT,
  isHDFCUAMEnabled = false,
  currentModelStatus = EMPTY_OBJECT,
  usersList = EMPTY_ARRAY,
}) => {
  const frontendConfig = useSelector((state) => state?.tenant?.frontendConfig);

  const { formSchema, initialFormValues } = useMemo(() => {
    if (!attribute) return { formSchema: {}, initialFormValues: {} };

    const attributes = [attribute];
    const currentValues = { [attribute.id]: value };

    return {
      formSchema: createFormSchema(attributes, {
        defaultTextMaxLength: frontendConfig?.defaultTextMaxLength,
      }),
      initialFormValues: createInitialFormValues(attributes, currentValues),
    };
  }, [attribute, value, frontendConfig]);

  const entityDetailsFormikForm = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: formSchema,
    onSubmit: (values) => {
      const saveSearch =
        attribute?.data_type === CHECKLIST_DATA_TYPE &&
        attribute?.checklist_type === CHECKLIST_TYPES?.SAVE_SEARCH;
      const attributeValue = values[attribute?.id];
      if (onSave) {
        let transformedValue = structuredClone(attributeValue);
        // If the attribute is a Document type, we need to set it as an array of document IDs to send it in payload
        if (attribute?.data_type === 'Document') {
          transformedValue = Array.isArray(attributeValue)
            ? attributeValue.map((doc) => doc?.document_id)
            : [];
        } else if (attribute?.data_type === 'Association') {
          // for association type attribute send entity_id and entity_type, as BE is expecting those fields for association type attribute
          // attributeValue?.entity_id checking this empty because, we have to send the null to BE if no value selected
          transformedValue = !isValueEmpty(attributeValue?.entity_id)
            ? {
                entity_id: attributeValue?.entity_id,
                entity_type: attributeValue?.entity_type,
              }
            : null;
        } else if (attribute?.data_type === 'User') {
          // transform username into user_id, as BE is expecting id
          transformedValue =
            usersList?.find((user) => user?.username === attributeValue)
              ?.user_id ?? attributeValue;
        } else if (attribute?.data_type === 'AttributeList') {
          transformedValue = !isValueEmpty(attributeValue?.id)
            ? attributeValue?.id
            : null;
        } else if (attribute?.data_type === 'ParameterTableList') {
          transformedValue = !isValueEmpty(attributeValue?.id)
            ? attributeValue?.id
            : null;
        } else if (attribute?.data_type === 'EmailTemplateList') {
          transformedValue = !isValueEmpty(attributeValue?.email_template_id)
            ? attributeValue?.email_template_id
            : null;
        } else if (saveSearch) {
          // In case of checklist attribute don't send "entityComments" on save.
          // Which is redundant we are already saving comments on save button of comment box.
          delete transformedValue.entityComments;
        }
        onSave(attribute, transformedValue);
      }
      onCancel();
    },
  });

  const handleSave = useCallback(() => {
    entityDetailsFormikForm.handleSubmit();
  }, [entityDetailsFormikForm]);

  const handleCancel = useCallback(() => {
    entityDetailsFormikForm.resetForm();
    if (onCancel) {
      onCancel();
    }
  }, [entityDetailsFormikForm, onCancel]);

  const handleValueChange = useCallback(
    (newValue) => {
      if (attribute?.id) {
        entityDetailsFormikForm.setFieldValue(attribute.id, newValue);
        if (
          isHDFCUAMEnabled &&
          HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES.includes(
            currentModelStatus?.currentState
          )
        ) {
          // Validate deployment date
          validateDeploymentDate({
            attribute,
            newValue,
            formDetails,
            attributeValues,
          });

          // Validate deployment date communication delay
          validateDeploymentDateCommunication({
            attribute,
            newValue,
            formDetails,
            attributeValues,
          });
        }
      }
    },
    [
      entityDetailsFormikForm,
      attribute?.id,
      attribute,
      formDetails,
      attributeValues,
      isHDFCUAMEnabled,
      currentModelStatus,
    ]
  );

  const getCurrentValue = useCallback(() => {
    return entityDetailsFormikForm.values[attribute?.id];
  }, [entityDetailsFormikForm.values, attribute?.id]);

  return {
    // Formik instance
    entityDetailsFormikForm,

    // Form state
    formSchema,
    initialFormValues,

    // Handlers
    handleSave,
    handleCancel,
    handleValueChange,

    // Getters
    getCurrentValue,
  };
};

export default useAttributeEditForm;
